import * as React from 'react'
import { useState, useEffect } from 'react'

import {
  FormValue,
  FormBlueprint,
  FormChangeHandler,
  IFormValidation,
  FormGuidelinesType,
  emptyFormGuidelines,
  formValidation,
} from '../../interfaces/components/dynamic-form'
import DynamicForm, { DisabledFields } from './Form'
import Loader from '../../components/Loader'
import LoadingOverlay from '../../components/LoadingOverlay'
import { Typography } from '@design/pt-ui'
import { translateWithUserLocale } from '../../utils/i18n'
import { Creative } from '../../interfaces/entities/creative'

export type ChangeCallback = (formState: {
  blueprint: FormBlueprint
  validation: IFormValidation
  value: FormValue
}) => any

export type Props = {
  label: React.ReactNode
  initializer: () => Promise<{
    value: FormValue
    blueprint: FormBlueprint
    validation: IFormValidation
    guidelines: FormGuidelinesType
    changeHandler: FormChangeHandler
  }>

  creativeId?: string
  creative?: Creative
  platform: string
  disabledFields?: DisabledFields
  locked?: boolean
  disabledOptionTooltip?: string
  hideErrorsUntilTouched?: boolean

  onValidationStart?: () => any
  onValidationEnd?: () => any

  colorizeNesting: boolean
  autoAddToEmptyArrays: boolean
  onChange: ChangeCallback
  onClose?: () => any
}

type State = {
  locked: boolean
  loading: boolean
  touched: boolean

  error: string | null

  value: FormValue
  validation: IFormValidation
  guidelines: FormGuidelinesType
  blueprint: FormBlueprint | null
  changeHandler: FormChangeHandler | null
}

const DancingDots = ({ max = 3 }) => {
  const [numberOfDots, setNumberOfDots] = useState(max)

  useEffect(() => {
    const interval = setInterval(() => {
      setNumberOfDots((numberOfDots) =>
        numberOfDots === max ? 1 : numberOfDots + 1,
      )
    }, 300)

    return () => clearInterval(interval)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return <>{'.'.repeat(numberOfDots)}</>
}

const getInitialState = (props: Props): State => {
  return {
    locked: false,
    loading: false,
    touched: false,
    error: null,
    value: {},
    validation: formValidation(),
    guidelines: emptyFormGuidelines(),
    blueprint: null,
    changeHandler: null,
  }
}

export default function LiveForm(props: Props) {
  const [state, setState] = useState(getInitialState(props))

  const initialize = async () => {
    try {
      setState((state) => ({
        ...state,
        loading: true,
      }))
      const response = await props.initializer()
      setState((state) => ({
        ...state,
        loading: false,
        value: response.value,
        blueprint: response.blueprint,
        validation: response.validation,
        guidelines: response.guidelines,
        changeHandler: response.changeHandler,
      }))
    } catch (e) {
      console.error(e)
      setState((state) => ({
        ...state,
        error: 'Error initializing form.',
      }))
    }
  }

  const onPendingChange = (pending: boolean) => {
    if (pending) {
      if (props.onValidationStart) {
        props.onValidationStart()
      }
    } else {
      if (props.onValidationEnd) {
        props.onValidationEnd()
      }
    }
  }

  React.useEffect(() => {
    initialize()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = async (value: FormValue) => {
    if (state.locked) {
      return
    }

    if (state.touched === false) {
      setState((state) => ({
        ...state,
        touched: true,
      }))
    }

    if (state.changeHandler && state.blueprint) {
      setState((state) => ({
        ...state,
        value,
        locked: true,
        loading: false,
      }))
      if (props.onValidationStart) {
        props.onValidationStart()
      }
      try {
        const response = await state.changeHandler(state.blueprint, value)

        const formState = {
          value: response.value,
          validation: response.validation,
          blueprint: response.blueprint,
        }

        setState((state) => ({
          ...state,
          locked: false,
          loading: false,
          ...formState,
        }))

        if (props.onValidationEnd) {
          props.onValidationEnd()
        }
        props.onChange(formState)
      } catch (e) {
        if (props.onValidationEnd) {
          props.onValidationEnd()
        }
        setState((state) => ({ ...state, locked: false, loading: false }))
      }
    }
  }

  const getValidation = (): IFormValidation => {
    if (props.locked) {
      return { ...state.validation, validUntil: -1 }
    }

    if (!state.touched && props.hideErrorsUntilTouched) {
      return { fields: {}, validUntil: 'end' }
    }

    return state.validation
  }

  if (state.error) {
    return <span>{state.error}</span>
  }

  if (state.loading) {
    return (
      <Loader
        variant="overlay"
        message={translateWithUserLocale('loaders.loading_form')}
      />
    )
  }

  if (state.blueprint == null || state.changeHandler == null) {
    return null
  }

  return (
    <LoadingOverlay
      backgroundColor={[230, 230, 230, 0.4]}
      active={state.locked}>
      <DynamicForm
        label={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              alignContent: 'center',
              marginBottom: 25,
            }}>
            {props.label}

            {state.locked ? (
              <Typography
                variant="h6"
                color="secondary"
                sx={{ marginLeft: '20px' }}>
                {translateWithUserLocale('dynamic_form.validating')}{' '}
                <DancingDots max={5} />
              </Typography>
            ) : null}
          </div>
        }
        onPendingValidationChange={onPendingChange}
        colorizeNesting={props.colorizeNesting}
        autoAddToEmptyArrays={props.autoAddToEmptyArrays}
        validation={getValidation()}
        guidelines={state.guidelines}
        disabledFields={props.disabledFields}
        locked={state.locked}
        value={state.value}
        disabledOptionTooltip={props.disabledOptionTooltip}
        blueprint={state.blueprint}
        onChange={handleChange}
        onClose={props.onClose}
        creativeId={props.creativeId}
        creative={props.creative}
        platform={props.platform}
      />
    </LoadingOverlay>
  )
}
